import type { DailySalesReport } from '../../../../api/daily-sales';
import { KpiStrip } from '../../primitives';
import { includesAny, RETAIL_SOURCES, TICKET_SOURCES } from '../daily-sales-filters';
import {
  countNoun,
  formatCount,
  summaryCells,
  windowSum,
  type SourceKey,
} from '../daily-sales-format';

import { DailySalesChart } from './DailySalesChart';
import { DailySalesFreshness } from './DailySalesFreshness';
import { DailySalesLedger } from './DailySalesLedger';
import { DailySalesNotes } from './DailySalesNotes';
import { DailySalesNotices } from './DailySalesNotices';
import { DailySalesRegions } from './DailySalesRegions';
import { DailySalesRevenueChart } from './DailySalesRevenueChart';

interface DailySalesBodyProps {
  report: DailySalesReport;
  /** The sources the chosen sale types and region leave on show. */
  sources: SourceKey[];
  onRegion: (regionId: string) => void;
}

export function DailySalesBody({ report, sources, onRegion }: DailySalesBodyProps) {
  const shared = { days: report.days, today: report.today };
  const noun = countNoun(sources);
  const countCharts: Array<{
    title: string;
    sources: SourceKey[];
    measure: 'bookings' | 'tickets';
    unit: string;
  }> = [
    {
      title: `${noun.charAt(0).toUpperCase()}${noun.slice(1)} per day`,
      sources,
      measure: 'bookings',
      unit: noun,
    },
    {
      title: 'Tickets per day',
      sources: TICKET_SOURCES.filter((s) => sources.includes(s)),
      measure: 'tickets',
      unit: 'tickets',
    },
    {
      title: 'Retail items per day',
      sources: RETAIL_SOURCES.filter((s) => sources.includes(s)),
      measure: 'tickets',
      unit: 'items',
    },
  ];

  return (
    <>
      <DailySalesFreshness report={report} sources={sources} />
      <DailySalesNotices report={report} sources={sources} />
      <div className="nb-ds-kpis">
        <KpiStrip items={summaryCells(report, sources)} />
      </div>
      {countCharts
        .filter((c) => c.sources.length > 0)
        .map((c) => (
          <DailySalesChart
            key={c.title}
            {...shared}
            title={c.title}
            sources={c.sources}
            measure={c.measure}
            format={formatCount}
            aside={
              <span className="nb-ds-meta">
                {formatCount(windowSum(report.days, c.measure, c.sources))} {c.unit}
              </span>
            }
          />
        ))}
      <DailySalesRevenueChart {...shared} sources={sources} />
      {includesAny(sources, TICKET_SOURCES) && (
        <DailySalesRegions rows={report.regionSales} region={report.region} onRegion={onRegion} />
      )}
      <DailySalesLedger {...shared} sources={sources} />
      <DailySalesNotes />
    </>
  );
}
